/**
 * Standardised "← Back to game" affordance shared by Rosters and BoxScore
 * full-page views. Rendered as the first focusable element on those pages
 * (autoFocus on mount) so keyboard tab-flow starts somewhere meaningful —
 * compensates for the focus-trap/restore guarantees the Modal previously
 * owned when these were modal-mounted.
 *
 * Uses an explicit route to `/games/:pk`. `useNavigate(-1)` would be more
 * compact but breaks for cold deep-links (refresh on /games/:pk/rosters
 * has no history to pop).
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './BackToGameLink.module.css';

interface BackToGameLinkProps {
  gamePk: number | undefined;
  /** When true, focuses this link on mount (keyboard entry point). */
  autoFocus?: boolean;
  /** Keeps a historical game's back-navigation in historical mode. */
  mode?: 'live' | 'historical';
}

export default function BackToGameLink({
  gamePk,
  autoFocus = true,
  mode = 'live',
}: BackToGameLinkProps): JSX.Element | null {
  const ref = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  if (!gamePk) return null;
  return (
    <Link
      ref={ref}
      to={`/games/${gamePk}${mode === 'historical' ? '?mode=historical' : ''}`}
      className={styles.link}
      data-testid="back-to-game"
    >
      ← Back to game
    </Link>
  );
}
